/**
 * 方向映射表
 */
const DIRECTION_MAP = {
    UP: 'up',
    DOWN: 'down',
    LEFT: 'left',
    RIGHT: 'right',
};

/**
 * 文字DOM深度克隆类
 * 支持上下左右方向的克隆
 */
class FJTextDomClone {
    _opacity = 1;

    constructor(props = {}) {
        // 容器DOM元素
        this.container = props.container;

        // 按方向存储克隆元素包裹层，每个方向只存储一个
        this.clonedElements = {
            up: null,
            down: null,
            left: null,
            right: null,
        };

        // 克隆属性控制
        this.cloneProperties = {
            isCloneUp: false,
            isCloneDown: false,
            isCloneLeft: false,
            isCloneRight: false,
        };
    }

    /**
     * 克隆文本DOM元素
     * @param {HTMLElement} container - 要遍历的容器DOM元素
     */
    cloneTextDom(container) {
        if (!container) return [];

        // 先删除四个方向容器内的所有元素
        this._clearAllDirections();

        // 查找所有class为'fl-text-base'的元素
        const textElements = container.querySelectorAll('.fl-text-base');
        // const textBackgroundElement = container.querySelector('.fl-text-background');

        if (textElements.length === 0) {
            return [];
        }
        const activeDirections = this._getActiveDirections();

        // 遍历每个文本元素
        textElements.forEach((element, index) => {
            // 获取激活的方向

            if (activeDirections.length === 0) {
                return;
            }

            // 为每个激活的方向克隆元素
            activeDirections.forEach(dir => {
                let wrapper = this.clonedElements[dir];
                let allStyleContent = null;

                // 如果该方向没有容器，创建新的包裹层
                if (!wrapper) {
                    wrapper = document.createElement('div');
                    wrapper.className = `fj-text-clone-wrapper fj-text-clone-${dir}`;
                    wrapper.style.position = 'absolute';
                    wrapper.style.pointerEvents = 'none';
                    wrapper.style.width = '100%';
                    wrapper.style.height = '100%';
                    wrapper.style.opacity = '0';

                    // 应用方向变换
                    this._applyDirectionAbsolute(wrapper, dir);

                    // 添加到容器中
                    if (this.container) {
                        this.container.appendChild(wrapper);
                    }

                    // 存储包裹层到对应方向
                    this.clonedElements[dir] = wrapper;
                }

                allStyleContent = wrapper.querySelector('.fj-text-clone-style-content');
                if (!allStyleContent) {
                    // 创建所有样式内容容器 同步文字的样式
                    allStyleContent = document.createElement('div');
                    allStyleContent.className = 'fj-text-clone-style-content';
                    allStyleContent.style.position = 'absolute';
                    allStyleContent.style.pointerEvents = 'none';
                    allStyleContent.style.width = '100%';
                    allStyleContent.style.height = '100%';
                    wrapper.appendChild(allStyleContent);
                }

                // 创建新的克隆
                const cloned = this._deepClone(element);

                // 特殊处理：如果不是最后一个元素，创建style-content容器
                if (index < textElements.length - 1) {
                    let styleContent = wrapper.querySelector('.style-content');

                    // 如果style-content容器不存在，创建它
                    if (!styleContent) {
                        styleContent = document.createElement('div');
                        styleContent.className = 'style-content';
                        styleContent.style.position = 'absolute';
                        styleContent.style.pointerEvents = 'none';
                        styleContent.style.width = '100%';
                        styleContent.style.height = '100%';
                        allStyleContent.appendChild(styleContent);
                    }

                    // 将克隆元素添加到style-content容器中
                    styleContent.appendChild(cloned);
                } else {
                    // 最后一个元素直接添加到wrapper中
                    allStyleContent.appendChild(cloned);
                }
            });
        });
    }

    /**
     * 控制克隆元素的显示隐藏
     * @param {boolean} visible - 是否显示
     * @param {string} direction - 指定方向，如果不指定则控制所有方向
     */
    toggleVisibility(visible, direction = null) {
        if (direction) {
            // 控制指定方向
            if (this.clonedElements[direction]) {
                this.clonedElements[direction].style.display = visible ? 'block' : 'none';
            }
        } else {
            // 控制所有方向
            Object.values(this.clonedElements).forEach(wrapper => {
                if (wrapper) {
                    wrapper.style.display = visible ? 'block' : 'none';
                }
            });
        }
    }

    /**
     * 销毁克隆元素
     * @param {string} direction - 指定方向，如果不指定则销毁所有方向
     */
    destroy(direction = null) {
        if (direction) {
            // 销毁指定方向
            if (this.clonedElements[direction]) {
                this._destroyClone(this.clonedElements[direction]);
                this.clonedElements[direction] = null;
            }
        } else {
            // 销毁所有方向
            Object.keys(this.clonedElements).forEach(dir => {
                if (this.clonedElements[dir]) {
                    this._destroyClone(this.clonedElements[dir]);
                    this.clonedElements[dir] = null;
                }
            });
        }
    }

    /**
     * 销毁指定方向的容器（当该方向克隆关闭时）
     * @param {string} direction - 要销毁的方向
     */
    destroyDirection(direction) {
        if (this.clonedElements[direction]) {
            this._destroyClone(this.clonedElements[direction]);
            this.clonedElements[direction] = null;
        }
    }

    /**
     * 设置克隆属性
     * @param {Object} properties - 克隆属性对象
     * @param {boolean} properties.isCloneUp - 是否向上克隆
     * @param {boolean} properties.isCloneDown - 是否向下克隆
     * @param {boolean} properties.isCloneLeft - 是否向左克隆
     * @param {boolean} properties.isCloneRight - 是否向右克隆
     */
    setCloneProperties(properties) {
        Object.assign(this.cloneProperties, properties);
    }

    /**
     * 获取克隆属性
     * @returns {Object} 克隆属性对象
     */
    getCloneProperties() {
        return { ...this.cloneProperties };
    }

    /**
     * 设置单个克隆属性
     * @param {string} property - 属性名
     * @param {boolean} value - 属性值
     */
    setCloneProperty(property, value) {
        if (this.cloneProperties.hasOwnProperty(property)) {
            this.cloneProperties[property] = value;
        } else {
            throw new Error(`不支持的属性: ${property}`);
        }
    }

    /**
     * 获取单个克隆属性
     * @param {string} property - 属性名
     * @returns {boolean} 属性值
     */
    getCloneProperty(property) {
        if (this.cloneProperties.hasOwnProperty(property)) {
            return this.cloneProperties[property];
        } else {
            throw new Error(`不支持的属性: ${property}`);
        }
    }

    /**
     * 深度克隆DOM元素
     * @private
     * @param {HTMLElement} element - 要克隆的DOM元素
     * @returns {HTMLElement} 克隆后的DOM元素
     */
    _deepClone(element) {
        // 直接克隆节点，不复制样式（样式是通用的）
        return element.cloneNode(true);
    }

    /**
     * 应用方向变换
     * @private
     * @param {HTMLElement} element - 要变换的元素
     * @param {string} direction - 方向
     * @param {Object} options - 选项
     */
    _applyDirectionAbsolute(element, direction, options) {
        let top = 0;
        let left = 0;

        switch (direction) {
            case 'up':
                top = `${-100}%`;
                break;
            case 'down':
                top = `100%`;
                break;
            case 'left':
                left = `${-100}%`;
                break;
            case 'right':
                left = `${100}%`;
                break;
        }

        element.style.top = top;
        element.style.left = left;
        element.style.position = 'absolute';
        element.style.pointerEvents = 'none';
    }

    /**
     * 获取激活的克隆方向
     * @private
     * @returns {Array<string>} 激活的方向数组
     */
    _getActiveDirections() {
        const directions = [];
        if (this.cloneProperties.isCloneUp) directions.push('up');
        if (this.cloneProperties.isCloneDown) directions.push('down');
        if (this.cloneProperties.isCloneLeft) directions.push('left');
        if (this.cloneProperties.isCloneRight) directions.push('right');
        return directions;
    }

    /**
     * 设置容器
     * @param {HTMLElement} container - 容器DOM元素
     */
    setContainer(container) {
        this.container = container;
    }

    /**
     * 获取容器
     * @returns {HTMLElement} 容器DOM元素
     */
    getContainer() {
        return this.container;
    }

    /**
     * 清空所有方向的克隆元素内容
     * @private
     */
    _clearAllDirections() {
        Object.keys(this.clonedElements).forEach(dir => {
            if (this.clonedElements[dir]) {
                // 只清空容器内容，不删除容器本身
                this.clonedElements[dir].innerHTML = '';
            }
        });
    }

    /**
     * 销毁单个克隆元素包裹层
     * @private
     * @param {HTMLElement} wrapper - 要销毁的包裹层元素
     */
    _destroyClone(wrapper) {
        if (wrapper && wrapper.parentNode) {
            wrapper.parentNode.removeChild(wrapper);
        }
    }

    getCloneElements() {
        return this.clonedElements;
    }

    /**
     * 更新克隆元素的样式
     * @returns {void}
     */
    updateStyle() {
        Object.keys(this.clonedElements).forEach(dir => {
            if (this.clonedElements[dir]) {
                const allStyleContent = this.clonedElements[dir].querySelector('.fj-text-clone-style-content');
                if (allStyleContent) {
                    allStyleContent.style.opacity = `${this._opacity}`;
                }
            }
        });
    }

    set opacity(opacity) {
        this._opacity = opacity;
        this.updateStyle();
    }

    get opacity() {
        return this._opacity;
    }
}

export default FJTextDomClone;
